import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  ChevronUp,
  ChevronDown,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Upload,
  Check,
} from 'lucide-react'
import Button from './Button'
import { Dropdown, DropdownItem } from './Dropdown'
import { Badge } from './Badge'
import { SearchInput } from './SearchInput'
import Select from './Select'
import { DEFAULT_PAGE_SIZE } from '@/helpers'
import { exportRowsToXlsx } from '@/utils/exporters'

/* ---------- Types ---------- */
export interface DataTableColumn {
  key: string
  label: string
  sortable?: boolean
  width?: string
  render?: (value: any, row: any) => React.ReactNode
}

export interface DataTableFilter {
  key: string
  label: string
  type: 'select' | 'multi-select' | 'date' | 'search'
  options?: { value: string; label: string }[]
  placeholder?: string
}

export interface DataTableWithFiltersProps {
  title: string
  columns: DataTableColumn[]
  data: any[]
  filters?: DataTableFilter[]
  showFilters?: boolean
  showDateRangeFilter?: boolean
  showExport?: boolean
  showSearch?: boolean
  onRowSelect?: (selectedRows: string[]) => void
  onRowEdit?: (row: any) => void
  onRowDelete?: (row: any) => void
  onSearch?: (query: string) => void
  onFilterChange?: (filters: any) => void
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  loading?: boolean
  totalItems?: number
  itemsPerPage?: number
  itemsPerPageOptions?: number[]
  currentPage?: number
  onPageChange?: (page: number) => void
  onItemsPerPageChange?: (itemsPerPage: number) => void
  onExport?: () => void
  searchDebounceMs?: number
  asyncFetchAllDataForExport?: (params: any) => Promise<any>
  searchPlaceholder?: string
  /**
   * When provided, each row gets an expand toggle chevron.
   * The returned node is rendered in a full-width row beneath the data row.
   */
  expandedRowRender?: (row: any) => React.ReactNode
  /** Key used to derive a stable row identity (default: 'id') */
  rowKey?: string
}

/* ---------- Sample data ---------- */
const sampleLeads = [
  { id: '1', name: 'Collins', company: 'BrightWave Innovations', companyIcon: '🟣', phone: '(123) 4567 890', email: 'anthony@example.com', status: 'Closed', createdDate: '2024-01-14', owner: 'Hendry Milner' },
  { id: '2', name: 'Konopelski', company: 'Stellar Dynamics', companyIcon: '🟢', phone: '(179) 7382 829', email: 'brian@example.com', status: 'Contacted', createdDate: '2024-01-21', owner: 'Guilory Berggren' },
  { id: '3', name: 'Adams', company: 'Quantum Nexus', companyIcon: '🔵', phone: '(184) 2719 738', email: 'harvey@example.com', status: 'Lost', createdDate: '2024-02-20', owner: 'Jami Carlile' },
  { id: '4', name: 'Schumm', company: 'EcoVision Enterprises', companyIcon: '🟢', phone: '(156) 2345 678', email: 'david@example.com', status: 'Not Contacted', createdDate: '2024-03-15', owner: 'Hendry Milner' },
  { id: '5', name: 'Wisozk', company: 'Aurora Technologies', companyIcon: '🟣', phone: '(189) 8765 432', email: 'emma@example.com', status: 'Contacted', createdDate: '2024-04-10', owner: 'Guilory Berggren' },
]

const statusColors: Record<string, string> = {
  Closed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Contacted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  Lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  'Not Contacted': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
}

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = Array.from(
  { length: 10 },
  (_, index) => (index + 1) * 10,
)

/* ---------- Date helpers ---------- */
type PresetKey = 'all' | '7' | '30' | '90' | 'custom' | null

const fmtBtnRange = (from: Date, to: Date) =>
  `${from.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })} – ${to.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}`

const toISODate = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${da}`
}

const addDays = (d: Date, days: number) => {
  const x = new Date(d)
  x.setDate(x.getDate() + days)
  return x
}
const endOfToday = () => {
  const t = new Date()
  t.setHours(23, 59, 59, 999)
  return t
}
const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const toDateInputValue = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${da}`
}
const parseDateInput = (v: string) => {
  const [y, m, d] = v.split('-').map(Number)
  const year = typeof y === 'number' && Number.isFinite(y) ? y : new Date().getFullYear()
  const month = typeof m === 'number' && Number.isFinite(m) ? m : 1
  const day = typeof d === 'number' && Number.isFinite(d) ? d : 1

  return new Date(year, month - 1, day)
}
const endOfDayLocal = (d: Date) => {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

type PaginationItem = number | 'ellipsis-left' | 'ellipsis-right'

const getVisiblePaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const visiblePages = new Set<number>([1, totalPages, currentPage])

  if (currentPage <= 4) {
    for (let page = 2; page <= 5; page += 1) {
      visiblePages.add(page)
    }
  } else if (currentPage >= totalPages - 3) {
    for (let page = totalPages - 4; page < totalPages; page += 1) {
      visiblePages.add(page)
    }
  } else {
    visiblePages.add(currentPage - 1)
    visiblePages.add(currentPage + 1)
  }

  const pages = Array.from(visiblePages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b)

  return pages.reduce<PaginationItem[]>((items, page, index) => {
    const previousPage = pages[index - 1]

    if (previousPage && page - previousPage === 2) {
      items.push(previousPage + 1)
    } else if (previousPage && page - previousPage > 2) {
      items.push(page < currentPage ? 'ellipsis-left' : 'ellipsis-right')
    }

    items.push(page)
    return items
  }, [])
}

/* ---------- Component ---------- */
export const DataTableWithFilters: React.FC<DataTableWithFiltersProps> = ({
  title,
  columns,
  data = sampleLeads,
  filters = [],
  showFilters = true,
  showDateRangeFilter = true,
  showExport = true,
  showSearch = true,
  onRowSelect,
  onRowEdit,
  onRowDelete,
  onSearch,
  onFilterChange,
  onSort,
  loading = false,
  totalItems = data.length,
  itemsPerPage = DEFAULT_PAGE_SIZE,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  currentPage = 1,
  onPageChange,
  onItemsPerPageChange,
  onExport,
  searchDebounceMs = 400,
  asyncFetchAllDataForExport,
  searchPlaceholder,
  expandedRowRender,
  rowKey = 'id',
}) => {
  // Table state
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortColumn, setSortColumn] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Expanded rows state (only relevant when expandedRowRender is provided)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRowExpand = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }

  // Date range filter state
  const [preset, setPreset] = useState<PresetKey>(null)
  const [dateFrom, setDateFrom] = useState<Date>(startOfDay(addDays(new Date(), -6)))
  const [dateTo, setDateTo] = useState<Date>(endOfToday())
  const [showCustom, setShowCustom] = useState(false)
  const [dateFromInput, setDateFromInput] = useState<string>(toDateInputValue(dateFrom))
  const [dateToInput, setDateToInput] = useState<string>(toDateInputValue(dateTo))
  const [tableFilters, setTableFilters] = useState<Record<string, string | string[]>>({})
  const todayStr = toDateInputValue(new Date())

  // Debounce search to parent
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!onSearch) return
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      onSearch(searchQuery.trim())
    }, searchDebounceMs)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [searchQuery, searchDebounceMs, onSearch])

  // Close custom popover on outside click / Escape
  const popRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!showCustom) return
    const onDocClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setShowCustom(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowCustom(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showCustom])

  const buttonLabel = useMemo(() => {
    if (preset === '7') return 'Last 7 Days'
    if (preset === '30') return 'Last 30 Days'
    if (preset === '90') return 'Last 90 Days'
    if (preset === 'custom') return fmtBtnRange(dateFrom, dateTo)
    if (preset === 'all') return 'All time'
    return 'Date range'
  }, [preset, dateFrom, dateTo])

  const safeItemsPerPage = Math.max(1, itemsPerPage)
  const pageSizeOptions = useMemo(
    () => Array.from(new Set([...itemsPerPageOptions, safeItemsPerPage]))
      .filter((option) => Number.isFinite(option) && option > 0)
      .sort((a, b) => a - b),
    [itemsPerPageOptions, safeItemsPerPage],
  )
  const totalPages = Math.max(1, Math.ceil(totalItems / safeItemsPerPage))
  const currentPageInRange = Math.min(Math.max(currentPage, 1), totalPages)
  const paginationItems = useMemo(
    () => getVisiblePaginationItems(currentPageInRange, totalPages),
    [currentPageInRange, totalPages]
  )
  const startItem = (currentPageInRange - 1) * safeItemsPerPage + 1
  const endItem = Math.min(currentPageInRange * safeItemsPerPage, totalItems)

  /* ---------- Handlers ---------- */
  const handleSelectAll = (checked: boolean) => {
    const ids = checked ? data.map((r) => r[rowKey]) : []
    setSelectedRows(ids)
    onRowSelect?.(ids)
  }

  const handleRowSelect = (rowId: string, checked: boolean) => {
    const newSelected = checked
      ? [...selectedRows, rowId]
      : selectedRows.filter((id) => id !== rowId)
    setSelectedRows(newSelected)
    onRowSelect?.(newSelected)
  }

  const handleSort = (columnKey: string) => {
    const newDir = sortColumn === columnKey && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortColumn(columnKey)
    setSortDirection(newDir)
    onSort?.(columnKey, newDir)
  }

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextItemsPerPage = Number(event.target.value)

    if (!Number.isFinite(nextItemsPerPage) || nextItemsPerPage === safeItemsPerPage) {
      return
    }

    onItemsPerPageChange?.(nextItemsPerPage)
  }

  const handleTableFilterChange = (key: string, value: string | string[] | undefined) => {
    setTableFilters((prev) => {
      const next = { ...prev }
      const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value)

      if (hasValue) {
        next[key] = value
      } else {
        delete next[key]
      }

      return next
    })
    onFilterChange?.({ [key]: value, _ts: Date.now() })
  }

  const handleExportInit = async () => {
    if (asyncFetchAllDataForExport) {
      const exportData = await asyncFetchAllDataForExport({})
      exportRowsToXlsx(exportData, columns, {
        fileName: 'exported-data',
        sheetName: 'Data',
      })
    }
  }

  const applyPreset = (key: PresetKey) => {
    if (key === 'all') {
      setPreset('all')
      setShowCustom(false)
      onFilterChange?.({ dateRange: null, preset: 'all', _ts: Date.now() })
      return
    }
    const todayEnd = endOfToday()
    if (key === '7' || key === '30' || key === '90') {
      const days = Number(key) - 1
      const from = startOfDay(addDays(new Date(), -days))
      setDateFrom(from)
      setDateTo(todayEnd)
      setDateFromInput(toDateInputValue(from))
      setDateToInput(toDateInputValue(todayEnd))
      setPreset(key)
      onFilterChange?.({
        dateRange: { preset: key, dateFrom: toISODate(from), dateTo: toISODate(todayEnd) },
        _ts: Date.now(),
      })
      setShowCustom(false)
    } else if (key === 'custom') {
      setPreset('custom')
      setShowCustom(true)
    }
  }

  const applyCustom = () => {
    if (dateFromInput.length !== 10 || dateToInput.length !== 10) return
    let from = startOfDay(parseDateInput(dateFromInput))
    let to = endOfDayLocal(parseDateInput(dateToInput))
    if (from > to) {
      const tmp = from; from = to; to = tmp
      setDateFromInput(toDateInputValue(from))
      setDateToInput(toDateInputValue(to))
    }
    setDateFrom(from)
    setDateTo(to)
    onFilterChange?.({
      dateRange: { preset: 'custom', dateFrom: toISODate(from), dateTo: toISODate(to) },
      _ts: Date.now(),
    })
    setShowCustom(false)
  }

  const renderSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) return <ChevronUp className="w-4 h-4 text-gray-400" />
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />
  }

  const renderCell = (column: DataTableColumn, row: any) => {
    const value = row[column.key]
    if (column.render) return column.render(value, row)
    switch (column.key) {
      case 'company':
        return (
          <span className="flex items-center gap-2">
            <span className="text-lg">{row.companyIcon}</span>
            <span className="font-medium">{value}</span>
          </span>
        )
      case 'status':
        return (
          <Badge variant="secondary" className={statusColors[value as keyof typeof statusColors]}>
            {value}
          </Badge>
        )
      case 'actions':
        return (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onRowEdit?.(row)} className="p-1 h-8 w-8">
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onRowDelete?.(row)} className="p-1 h-8 w-8 text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )
      default:
        return <span>{value}</span>
    }
  }

  const renderFilterControl = (filter: DataTableFilter) => {
    const rawValue = tableFilters[filter.key]
    const value = typeof rawValue === 'string' ? rawValue : ''
    const selectedValues = Array.isArray(rawValue) ? rawValue : []

    if (filter.type === 'select') {
      return (
        <div key={filter.key} className="w-full sm:w-48">
          <Select
            aria-label={filter.label}
            size="md"
            value={value}
            onChange={(event) => handleTableFilterChange(filter.key, event.target.value)}
            options={[
              { value: '', label: filter.placeholder ?? `All ${filter.label}` },
              ...(filter.options ?? []),
            ]}
          />
        </div>
      )
    }

    if (filter.type === 'multi-select') {
      const selectedOptions = filter.options?.filter((option) =>
        selectedValues.includes(option.value),
      ) ?? []
      const firstSelectedLabel = selectedOptions[0]?.label
      const selectedLabel =
        selectedOptions.length === 0
          ? (filter.placeholder ?? `All ${filter.label}`)
          : selectedOptions.length === 1
            ? (firstSelectedLabel ?? filter.label)
            : `${selectedOptions.length} ${filter.label}s`

      return (
        <div key={filter.key} className="w-full sm:w-48">
          <Dropdown
            trigger={
              <Button
                aria-label={filter.label}
                variant="outline"
                size="md"
                fullWidth
                rightIcon={<ChevronDown className="w-4 h-4" />}
                className="h-10 rounded-md justify-between"
              >
                <span className="block min-w-0 truncate text-left">
                  {selectedLabel}
                </span>
              </Button>
            }
            items={[
              {
                label: filter.placeholder ?? `All ${filter.label}`,
                icon: selectedValues.length === 0
                  ? <Check className="w-4 h-4 text-blue-600" />
                  : <span className="w-4" />,
                onClick: () => handleTableFilterChange(filter.key, []),
              },
              ...((filter.options ?? []).map((option) => {
                const isSelected = selectedValues.includes(option.value)
                const nextValues = isSelected
                  ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
                  : [...selectedValues, option.value]

                return {
                  label: option.label,
                  icon: isSelected
                    ? <Check className="w-4 h-4 text-blue-600" />
                    : <span className="w-4" />,
                  onClick: () => handleTableFilterChange(filter.key, nextValues),
                }
              })),
            ]}
            align="right"
            width="md"
            usePortal={false}
            matchTriggerWidth
            closeOnSelect={false}
          />
        </div>
      )
    }

    if (filter.type === 'date') {
      return (
        <input
          key={filter.key}
          aria-label={filter.label}
          type="date"
          value={value}
          onChange={(event) => handleTableFilterChange(filter.key, event.target.value)}
          className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-44 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
      )
    }

    return (
      <div key={filter.key} className="w-full sm:w-56">
        <SearchInput
          value={value}
          placeholder={filter.placeholder ?? filter.label}
          onChange={(nextValue) => handleTableFilterChange(filter.key, nextValue.trim())}
          showClearButton
        />
      </div>
    )
  }

  const dateMenuItems: DropdownItem[] = [
    { label: 'All time', onClick: () => applyPreset('all'), className: preset === 'all' ? 'bg-gray-100 dark:bg-gray-700' : '' },
    { label: 'Last 7 Days', onClick: () => applyPreset('7'), className: preset === '7' ? 'bg-gray-100 dark:bg-gray-700' : '' },
    { label: 'Last 30 Days', onClick: () => applyPreset('30'), className: preset === '30' ? 'bg-gray-100 dark:bg-gray-700' : '' },
    { label: 'Last 90 Days', onClick: () => applyPreset('90'), className: preset === '90' ? 'bg-gray-100 dark:bg-gray-700' : '' },
    { label: 'Custom Range', onClick: () => { setPreset('custom'); setShowCustom(true) }, className: preset === 'custom' ? 'bg-gray-100 dark:bg-gray-700' : '' },
  ]

  // Total column count including the optional expand toggle column
  const totalColSpan = columns.length + (expandedRowRender ? 1 : 0)

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 relative">
      {/* Header */}
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col gap-4">

          {/* Title */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">

            {/* Search */}
            {showSearch && (
              <div className="w-full min-w-0 sm:w-72">
                <SearchInput
                  placeholder={searchPlaceholder || "Search..."}
                  className="w-full"
                  onChange={(v: string) => setSearchQuery(v)}
                />
              </div>
            )}

            {/* Filters */}
            {showFilters && (
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">

                {/* Date Dropdown */}
                {showDateRangeFilter && (
                  <div className="relative w-full sm:w-56">
                    <Dropdown
                      trigger={
                        <Button
                          variant="outline"
                          size="md"
                          fullWidth
                          rightIcon={<ChevronDown className="w-4 h-4" />}
                          className="h-10 overflow-hidden rounded-md justify-between px-3"
                        >
                          <span className="block min-w-0 max-w-full truncate text-left">
                            {buttonLabel}
                          </span>
                        </Button>
                      }
                      items={dateMenuItems}
                      align="right"
                      width="md"
                      usePortal={false}
                      matchTriggerWidth
                    />

                    {/* Custom Range Popover (UNCHANGED LOGIC) */}
                    {showCustom && (
                      <div
                        ref={popRef}
                        className={[
                          'absolute left-0 sm:left-auto sm:right-0 top-full mt-2 z-50',
                          'w-[calc(100vw-32px)] sm:w-[320px] max-w-[320px]',
                          'rounded-lg border border-gray-200 dark:border-gray-700',
                          'bg-white dark:bg-gray-800 shadow-lg p-4',
                        ].join(' ')}
                        role="dialog"
                        aria-modal="false"
                      >
                        <div className="text-sm font-semibold mb-3 text-gray-800 dark:text-gray-100">
                          Custom range
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <label className="text-xs text-gray-600 dark:text-gray-300">
                            From
                            <input
                              type="date"
                              className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                              value={dateFromInput}
                              max={todayStr}
                              onChange={(e) => {
                                const v = e.target.value
                                setDateFromInput(v)
                                if (v && v.length === 10) {
                                  setDateFrom(startOfDay(parseDateInput(v)))
                                }
                              }}
                            />
                          </label>

                          <label className="text-xs text-gray-600 dark:text-gray-300">
                            To
                            <input
                              type="date"
                              className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                              value={dateToInput}
                              max={todayStr}
                              onChange={(e) => {
                                const v = e.target.value
                                setDateToInput(v)
                                if (v && v.length === 10) {
                                  setDateTo(endOfDayLocal(parseDateInput(v)))
                                }
                              }}
                            />
                          </label>

                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowCustom(false)}
                            >
                              Cancel
                            </Button>

                            <Button
                              variant="primary"
                              size="sm"
                              onClick={applyCustom}
                            >
                              Apply
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {filters.map(renderFilterControl)}

                {/* Export Button */}
                {showExport && (
                  <Button
                    onClick={() => {
                      if (asyncFetchAllDataForExport) {
                        handleExportInit()
                      } else if (onExport) {
                        onExport()
                      }
                    }}
                    variant="outline"
                    size="md"
                    className="flex items-center gap-2"
                  >
                    Export
                    <Upload className="w-3 h-3 ml-1" />
                  </Button>
                )}

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-200">
            <tr>
              {/* Expand toggle header cell — only rendered when expandedRowRender is provided */}
              {expandedRowRender && (
                <th className="w-10 px-4 py-3" aria-label="Expand" />
              )}

              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-sm font-bold text-gray-900 dark:text-gray-400 capitalize tracking-wider ${column.sortable ? 'cursor-pointer hover:text-gray-700 dark:hover:text-gray-300' : ''}`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <span className="flex items-center gap-1">
                    <span>{column.label}</span>
                    {column.sortable && renderSortIcon(column.key)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="[&>tr.data-row:nth-child(odd)]:bg-gray-100 [&>tr.data-row:nth-child(even)]:bg-white divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={totalColSpan} className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={totalColSpan} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No data available
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const rowId = String(row[rowKey] ?? row.id ?? Math.random())
                const isExpanded = expandedRows.has(rowId)

                return (
                  <React.Fragment key={rowId}>
                    {/* ── Main data row ── */}
                    <tr className="data-row hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 text-sm">
                      {/* Expand toggle cell */}
                      {expandedRowRender && (
                        <td className="w-10 px-4 py-4">
                          <button
                            onClick={() => toggleRowExpand(rowId)}
                            className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-gray-700"
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          >
                            {isExpanded
                              ? <ChevronUp className="w-4 h-4" />
                              : <ChevronDown className="w-4 h-4" />
                            }
                          </button>
                        </td>
                      )}

                      {columns.map((column) => (
                        <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                          {renderCell(column, row)}
                        </td>
                      ))}
                    </tr>

                    {/* ── Expanded content row ── */}
                    {expandedRowRender && isExpanded && (
                      <tr className="bg-slate-50 dark:bg-gray-800/50">
                        {/* Empty cell under the toggle column */}
                        <td className="w-10 px-4 pt-0 pb-4 align-top" />
                        {/* Content spans all data columns */}
                        <td colSpan={columns.length} className="px-6 pt-2 pb-5">
                          {expandedRowRender(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">

            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
              {/* Showing entries text */}
              <div className="text-center text-sm text-gray-600 sm:text-left dark:text-gray-400">
                Showing {startItem} - {endItem} of {totalItems} entries
              </div>

              {onItemsPerPageChange && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Show</span>
                  <div className="w-20">
                    <Select
                      aria-label="Entries per page"
                      size="sm"
                      value={String(safeItemsPerPage)}
                      onChange={handleItemsPerPageChange}
                      options={pageSizeOptions.map((option) => ({
                        value: String(option),
                        label: String(option),
                      }))}
                      disabled={loading}
                    />
                  </div>
                  <span>entries</span>
                </div>
              )}
            </div>

            {/* Pagination buttons */}
            <div className="flex flex-wrap justify-center sm:justify-end items-center gap-2">

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPageInRange - 1)}
                disabled={currentPageInRange === 1}
                className="p-2"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {paginationItems.map((page) => {
                if (typeof page !== 'number') {
                  return (
                    <span
                      key={page}
                      className="flex h-8 min-w-[36px] items-center justify-center text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </span>
                  )
                }

                return (
                  <Button
                    key={page}
                    variant={page === currentPageInRange ? "primary" : "outline"}
                    size="sm"
                    onClick={() => onPageChange?.(page)}
                    className={`min-w-[36px] ${page === currentPageInRange
                        ? "bg-primary-button text-white hover:bg-primary-button"
                        : ""
                      }`}
                  >
                    {page}
                  </Button>
                )
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPageInRange + 1)}
                disabled={currentPageInRange === totalPages}
                className="p-2"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

            </div>
          </div>
        </div>
      )}
    </div>
  )
}
